import { Fragment, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { GetGameStateResult } from "results/frankenstories/GetGameStateResult";
import { ReplyDisplay } from "components/game/ReplyDisplay";
import { StimulusSidebar } from "components/game/StimulusSidebar";
import { CompletedGamePlayerStateResult } from "results/frankenstories/CompletedGamePlayerStateResult";
import { Link } from "react-router-dom";
import { RouteLink, RouteList } from "Routes";
import { GameStatePlayerDto } from "dtos/frankenstories/GameStatePlayerDto";
import { FrankenstoryStoryItemDto } from "dtos/frankenstories/FrankenstoryStoryItemDto";
import { StoryDetails } from "components/game/StoryDetails";
import { StoryRepliesDisplay } from "components/game/StoryRepliesDisplay";
import { TabButton } from "components/TabButton";
import { PageHeader } from "components/PageHeader";
import { IconText } from "components/IconText";
import { CopyStoryButton } from "components/story/CopyStoryButton";
import { CopyStoryLinkButton } from "components/story/CopyStoryLinkButton";
import { Leaderboard } from "components/story/Leaderboard";
import { ListItemPlaceholder } from "components/placeholders/ListItemPlaceholder";
import { PromptPlaceholder } from "components/placeholders/PromptPlaceholder";
import { TabsPlaceholder } from "components/placeholders/TabsPlaceholder";
import { RoundInstructionsDisplay } from "components/RoundInstructionsDisplay";
import { useDuplication } from "hooks/useDuplication";
import { ProBadge } from "components/subscriptions/ProBadge";
import { GlobalContext } from "contexts/GlobalContext";
import { setPageLoadMessage } from "pages/Page";
import { RewriteStory } from "components/game/RewriteStory";
import { getCharacter } from "utils/AssetManager";
import { ReportCardModal, ReportCardModalButton } from "components/modals/ReportCardModal";
import { ConditionalTooltip } from "components/ConditionalTooltip";
import { DeleteGameButton } from "components/buttons/DeleteGameButton";
import { RoundInstructionDto } from "dtos/frankenstories/RoundInstructionDto";
import { RoundDto } from "dtos/frankenstories/RoundDto";

export type PlayerDictionary = Record<number, string>;

const StoryStat = (props: { count?: number; icon: ReactNode; description: string }) => {
    const { count, icon, description } = props;

    return (
        <li className="hstack gap-2 pb-1">
            <span>{icon}</span>
            <span className="fw-bolder">{count == null ? <span className="fas fa-infinity"></span> : count}</span>
            <span>{description}</span>
        </li>
    );
};

const StoryItemPlaceholder = () => (
    <Fragment>
        <div className="g-col-12 g-col-lg-9">
            <ListItemPlaceholder size={7} />
        </div>

        <div className="g-col-12 g-col-lg-3">
            <div className="placeholder-glow">
                <div className="placeholder bg-white py-4 col-12"></div>
            </div>
        </div>
    </Fragment>
);

export const CompletedStory = (props: {
    gameState?: GetGameStateResult;
    playerState?: CompletedGamePlayerStateResult;
    code: string;
    isHallOfFame: boolean;
}) => {
    const { gameState, playerState, isHallOfFame } = props;

    const { isAuthenticated, globalData, userData } = useContext(GlobalContext);

    const { exportSettings } = useDuplication();

    const [selectedRound, setSelectedRound] = useState<RoundDto>();
    const [selectedPlayer, setSelectedPlayer] = useState<GameStatePlayerDto>();
    const [selectedReplies, setSelectedReplies] = useState<Array<FrankenstoryStoryItemDto>>([]);
    const [players, setPlayers] = useState<Array<GameStatePlayerDto>>([]);
    const [storyAlternatives, setStoryAlternatives] = useState<Array<FrankenstoryStoryItemDto>>([]);
    const [voteTally, setVoteTally] = useState<Record<number, number>>({});
    const [titleReply, setTitleReply] = useState<FrankenstoryStoryItemDto>();
    const [showingLeaderboard, setShowingLeaderboard] = useState(false);
    const [showingPopularReplies, setShowingPopularReplies] = useState(false);
    const [roundInstructions, setRoundInstructions] = useState<Array<RoundInstructionDto>>();

    // rewrite activity
    const [showingRewrite, setShowingRewrite] = useState(false);

    // should show leaderboard or not
    const [shouldShowLeaderboard, setShouldShowLeaderboard] = useState(false);

    useEffect(() => {
        if (gameState?.settings.showLeaderboard && gameState.playerCount > 2) {
            setShouldShowLeaderboard(true);
        }
    }, [gameState?.playerCount, gameState?.settings.showLeaderboard]);

    const getVoteCount = useCallback(
        (replyId: number) => {
            if (replyId in voteTally) {
                return voteTally[replyId];
            }

            return 0;
        },
        [voteTally],
    );

    const selectPlayer = (p: GameStatePlayerDto) => {
        setSelectedRound(undefined);

        // select round
        setSelectedPlayer(p);

        setShowingPopularReplies(false);
        setShowingLeaderboard(false);
        setShowingRewrite(false);
    };

    // round selector text
    const [currentRoundText, setCurrentRoundText] = useState<string>();

    useEffect(() => {
        if (selectedRound) {
            setCurrentRoundText(selectedRound.name);
        } else {
            setCurrentRoundText(undefined);
        }
    }, [selectedRound]);

    // set title reply
    useEffect(() => {
        const roundCount = gameState?.settings.roundCount;

        if (roundCount) {
            const t = gameState?.allReplies.find(x => x.roundNumber > roundCount && x.isWinner);
            setTitleReply(t);
        } else {
            setTitleReply(undefined);
        }
    }, [gameState?.allReplies, gameState?.settings.roundCount]);

    // handle player dictionary
    const playerNames = useMemo<PlayerDictionary>(() => {
        const names: PlayerDictionary = {};
        let i = 1;

        if (playerState && gameState) {
            for (const p of gameState.players) {
                const name = playerState.isPlayer || playerState.isLeader ? p.name : `Player ${i}`;

                names[p.id] = name;
                i++;
            }
        }

        return names;
    }, [gameState, playerState]);

    // subtitle
    const [subtitle, setSubtitle] = useState<string>();

    useEffect(() => {
        if (titleReply) {
            const { reactionCount, playerId } = titleReply;
            const playerName = playerNames[playerId];

            const voteCount = getVoteCount(titleReply.id);

            let namedBy = "";

            if (titleReply.isPerfect) {
                namedBy += "⭐ ";
            }

            namedBy += `Named by ${playerName}`;

            if (reactionCount && !voteCount) {
                // winning reply has reactions but no votes
                namedBy += ` with ${reactionCount} ❤️`;
            } else if (voteCount) {
                namedBy += ` with ${voteCount === 1 ? "1 vote" : `${voteCount} votes`}`;

                if (reactionCount) {
                    namedBy += ` and ${reactionCount} ❤️`;
                }
            }

            setSubtitle(namedBy);
        } else if (gameState) {
            setSubtitle("Untitled story");
        }
    }, [getVoteCount, titleReply, gameState, playerNames]);

    // update vote tally
    useEffect(() => {
        if (gameState?.votedReplyIds) {
            const tally: Record<number, number> = {};

            for (const id of gameState.votedReplyIds) {
                if (!(id in tally)) {
                    tally[id] = 0;
                }

                tally[id]++;
            }

            setVoteTally(tally);
        }
    }, [gameState?.votedReplyIds]);

    // filter replies
    useEffect(() => {
        let replies: Array<FrankenstoryStoryItemDto> = [];

        if (!selectedPlayer && !selectedRound && gameState?.story) {
            // default when nothing is selected is the story
            replies = gameState.story;
        } else if (gameState?.allReplies) {
            replies = gameState?.allReplies;

            if (selectedRound) {
                replies = replies.filter(x => x.roundId === selectedRound.id);
            }

            if (selectedPlayer) {
                replies = replies.filter(x => x.playerId === selectedPlayer.id);
            }
        }

        // sort
        replies.sort((a, b) => {
            // first order by round number
            if (a.roundNumber > b.roundNumber) {
                return 1;
            } else if (a.roundNumber < b.roundNumber) {
                return -1;
            }

            // order by name
            if (a.playerName.toLowerCase() > b.playerName.toLowerCase()) {
                return 1;
            } else if (a.playerName.toLowerCase() < b.playerName.toLowerCase()) {
                return -1;
            }

            // then order by name
            return 0;
        });

        setSelectedReplies(replies);
    }, [gameState?.allReplies, gameState?.story, selectedPlayer, selectedRound]);

    // set players
    useEffect(() => {
        if (gameState?.players) {
            const p = [...gameState.players].filter(x => gameState.allReplies.find(y => y.playerId === x.id));

            // if names aren't anonymous then sort by name
            if (playerState?.isPlayer) {
                p.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));
            } else {
                // otherwise sort by id or else it's weird after 10 players
                p.sort((a, b) => a.id - b.id);
            }

            setPlayers(p);
        }
    }, [gameState?.allReplies, gameState?.players, playerState?.isPlayer]);

    // set alternate replies
    useEffect(() => {
        if (gameState?.allReplies) {
            const alternates = [...gameState.allReplies].filter(x => !x.isWinner);

            alternates.sort((a, b) => getVoteCount(b.id) - getVoteCount(a.id));

            setStoryAlternatives(alternates.slice(0, 5));
        }
    }, [gameState?.allReplies, getVoteCount]);

    // round instructions
    useEffect(() => {
        if (gameState?.roundInstructions.length) {
            setRoundInstructions([...gameState.roundInstructions]);
        }
    }, [gameState?.roundInstructions]);

    const copyGame = () => {
        if (gameState && globalData?.isSubscribed) {
            setPageLoadMessage("Game settings copied");

            exportSettings({
                imageId: gameState.imageId,
                imageUrl: gameState.imageUrl,
                promptText: gameState.prompt,
                roundCount: gameState.settings.roundCount,
                roundInstructions: gameState.roundInstructions,
            });
        }
    };

    // player selector text
    const [currentPlayerText, setCurrentPlayerText] = useState<string>();

    useEffect(() => {
        if (selectedPlayer) {
            setCurrentPlayerText(playerNames[selectedPlayer.id]);
        } else {
            setCurrentPlayerText(undefined);
        }
    }, [selectedPlayer, playerNames]);

    // current round instructions
    const [roundInstruction, setRoundInstruction] = useState("");

    useEffect(() => {
        let foundInstruction = false;

        if (gameState?.roundInstructions.length && selectedRound) {
            // get round index
            const roundIndex = gameState?.rounds.indexOf(selectedRound);

            if (roundIndex !== undefined) {
                const selectedInstruction = gameState.roundInstructions[roundIndex];

                if (selectedInstruction) {
                    setRoundInstruction(selectedInstruction.name);
                    foundInstruction = true;
                }
            }
        }

        if (!foundInstruction) {
            setRoundInstruction("");
        }
    }, [gameState?.roundInstructions, gameState?.rounds, selectedRound]);

    const columnTitle = showingPopularReplies
        ? "Popular replies"
        : showingLeaderboard
        ? "Leaderboard"
        : showingRewrite
        ? "My version"
        : currentPlayerText
        ? currentPlayerText
        : currentRoundText
        ? currentRoundText
        : "Final story";

    const columnTitleIcon = showingPopularReplies ? (
        <span className="fas fa-heart"></span>
    ) : showingLeaderboard ? (
        <span className="fas fa-ranking-star"></span>
    ) : showingRewrite ? (
        <span className="fas fa-edit"></span>
    ) : currentPlayerText ? (
        <span className="fas fa-user"></span>
    ) : currentRoundText ? (
        <span className="fas fa-bell"></span>
    ) : (
        <span className="fas fa-ribbon"></span>
    );

    const nonTitleRoundCount = gameState?.rounds.filter(x => !x.isTitleRound).length ?? 0;
    const roundCountText = `round${nonTitleRoundCount === 1 ? "" : "s"}`;

    return (
        <div className="vstack gap-4 gap-lg-5">
            <PageHeader
                icon="🎮"
                title={gameState?.title ?? gameState?.gameName}
                description={subtitle}
                action={
                    gameState && (
                        <Fragment>
                            <ConditionalTooltip
                                show={true}
                                text={isHallOfFame ? "Back to Hall of Fame" : "Back to history"}
                            >
                                <Link
                                    to={isHallOfFame ? RouteList.DiscoverHallOfFame : RouteList.GamesHistory}
                                    className="btn btn-dark btn-circle"
                                >
                                    <span className="fal fa-arrow-left"></span>
                                </Link>
                            </ConditionalTooltip>

                            <CopyStoryButton gameState={gameState} playerState={playerState} />
                            <CopyStoryLinkButton gameState={gameState} />

                            {!gameState.isHomework && playerState?.isLeader && (
                                <DeleteGameButton
                                    code={gameState.code}
                                    placement="bottom"
                                    btnClass="btn btn-danger btn-circle"
                                    iconDanger={false}
                                    redirect={RouteList.GamesHistory}
                                />
                            )}
                        </Fragment>
                    )
                }
            />

            <div className="vstack gap-4 gap-lg-5">
                <div className="grid gap-4 gap-lg-5">
                    <div className="g-col-12 g-col-lg-4">
                        <div className="vstack gap-4">
                            <div className="vstack gap-3">
                                {gameState ? (
                                    <StimulusSidebar
                                        isImageHidden={gameState.isImageHidden}
                                        prompt={gameState.prompt}
                                        imageUrl={gameState.imageUrl}
                                        imageSource={gameState.imageSource}
                                    />
                                ) : (
                                    <PromptPlaceholder />
                                )}

                                {roundInstructions && roundInstructions.length > 0 && (
                                    <RoundInstructionsDisplay roundInstructions={roundInstructions} />
                                )}
                            </div>

                            <div className="hstack gap-2 justify-content-center">
                                {playerState?.isLeader && gameState?.code && !userData?.isStudent ? (
                                    <div className="dropdown">
                                        <Link
                                            to={RouteLink.gamesReport(gameState.code)}
                                            className="btn btn-dark icon-link"
                                        >
                                            <IconText icon="fas fa-chart-line" text="View report" />
                                        </Link>
                                    </div>
                                ) : (
                                    <Fragment>
                                        <button
                                            disabled={!gameState || !globalData?.isSubscribed}
                                            type="button"
                                            className="btn btn-dark icon-link"
                                            onClick={copyGame}
                                        >
                                            <IconText icon="fas fa-file-import" text="Use prompt" />
                                        </button>

                                        <ProBadge />
                                    </Fragment>
                                )}
                            </div>

                            {gameState?.replyCount && (
                                <div className="vstack gap-3 align-items-center align-items-lg-start">
                                    <h2 className="h4">Story details</h2>

                                    <ul className="ps-0 m-0">
                                        <StoryStat icon="🔔" count={nonTitleRoundCount} description={roundCountText} />

                                        <StoryStat
                                            icon="🧑"
                                            count={gameState.playerCount}
                                            description={gameState.playerCount === 1 ? "player" : "players"}
                                        />

                                        <StoryStat
                                            icon="💬"
                                            count={gameState.replyCount}
                                            description={gameState.replyCount === 1 ? "reply" : "replies"}
                                        />

                                        <StoryStat
                                            count={gameState.settings.writingTimer}
                                            description="seconds writing"
                                            icon="⏰"
                                        />

                                        <StoryStat
                                            count={gameState.settings.characterLimit}
                                            description="character limit"
                                            icon="🔤"
                                        />
                                    </ul>
                                </div>
                            )}

                            {!!playerState?.unlockedAvatarId && (
                                <div className="card">
                                    <div className="hstack">
                                        <img
                                            alt=""
                                            style={{ width: 80 }}
                                            className="align-self-end"
                                            src={getCharacter(playerState.unlockedAvatarId)}
                                        />

                                        <div className="card-body">
                                            <div className="h6 text-dark">You unlocked this avatar in this game!</div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {gameState?.isHallOfFameApproved && (
                                <div className="alert bg-success hstack gap-3 mb-0 auto-white-link">
                                    <div className="fs-2">
                                        <span className="fas fa-trophy"></span>
                                    </div>

                                    <div>
                                        This story is in the{" "}
                                        <Link className="text-nowrap" to={RouteList.DiscoverHallOfFame}>
                                            Hall of Fame
                                        </Link>
                                        !
                                    </div>
                                </div>
                            )}

                            {playerState?.llmScore && (
                                <div className="text-center">
                                    <ReportCardModalButton />
                                </div>
                            )}

                            {gameState?.socialLinks && gameState.socialLinks.length > 0 && (
                                <div className="vstack gap-3">
                                    <h2 className="h4 text-center text-lg-start">You can watch this one!</h2>

                                    <div className="d-flex gap-2 align-items-center flex-wrap justify-content-center justify-content-lg-start">
                                        {gameState.socialLinks.map(l => (
                                            <a
                                                className={`btn btn-${l.website.toLowerCase()} rounded-pill`}
                                                href={l.url}
                                                target="_blank"
                                                key={l.id}
                                                rel="noreferrer"
                                            >
                                                <span className={`fab fa-${l.website.toLowerCase()}`}></span>
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="g-col-12 g-col-lg-8">
                        <div className="vstack gap-3 align-items-center align-items-lg-start">
                            <div className="vstack gap-4">
                                {gameState ? (
                                    <div className="d-flex gap-2 flex-wrap justify-content-center justify-content-lg-start">
                                        <TabButton
                                            text={<span className="fal fa-ribbon"></span>}
                                            tooltip="Final story"
                                            isSelected={
                                                !selectedPlayer &&
                                                !selectedRound &&
                                                !showingRewrite &&
                                                !showingLeaderboard &&
                                                !showingPopularReplies
                                            }
                                            onClick={() => {
                                                setSelectedPlayer(undefined);
                                                setSelectedRound(undefined);
                                                setShowingLeaderboard(false);
                                                setShowingPopularReplies(false);
                                                setShowingRewrite(false);
                                            }}
                                        />

                                        {storyAlternatives.length > 0 && (
                                            <TabButton
                                                text={<span className="fal fa-heart"></span>}
                                                isSelected={showingPopularReplies}
                                                tooltip="Popular replies"
                                                onClick={() => {
                                                    setSelectedPlayer(undefined);
                                                    setSelectedRound(undefined);
                                                    setShowingPopularReplies(true);
                                                    setShowingLeaderboard(false);
                                                    setShowingRewrite(false);
                                                }}
                                            />
                                        )}

                                        <ConditionalTooltip show={true} text="Select round" placement="top">
                                            <div className="dropdown">
                                                <TabButton
                                                    dropdown={true}
                                                    isSelected={!!currentRoundText}
                                                    text={<span className="fal fa-bell"></span>}
                                                />

                                                <ul className="dropdown-menu dropdown-menu-end">
                                                    {gameState &&
                                                        gameState.rounds
                                                            .filter(x =>
                                                                gameState.allReplies.find(y => y.roundId === x.id),
                                                            )
                                                            .map(r => (
                                                                <li key={r.id}>
                                                                    <button
                                                                        type="button"
                                                                        className="dropdown-item"
                                                                        onClick={() => {
                                                                            setSelectedPlayer(undefined);

                                                                            // select round
                                                                            setSelectedRound(r);

                                                                            setShowingPopularReplies(false);
                                                                            setShowingLeaderboard(false);
                                                                            setShowingRewrite(false);
                                                                        }}
                                                                    >
                                                                        🔔 {r.name}
                                                                    </button>
                                                                </li>
                                                            ))}
                                                </ul>
                                            </div>
                                        </ConditionalTooltip>

                                        <ConditionalTooltip show={true} text="Select player" placement="top">
                                            <div className="dropdown">
                                                <TabButton
                                                    dropdown={true}
                                                    isSelected={!!currentPlayerText}
                                                    text={<span className="fal fa-user"></span>}
                                                />

                                                <ul className="dropdown-menu dropdown-menu-end">
                                                    {players.map(p => {
                                                        const playerName = playerNames[p.id];

                                                        return (
                                                            <li key={p.id}>
                                                                <button
                                                                    type="button"
                                                                    className="dropdown-item"
                                                                    onClick={() => {
                                                                        selectPlayer(p);
                                                                    }}
                                                                >
                                                                    🧑 {playerName}
                                                                </button>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                        </ConditionalTooltip>

                                        {shouldShowLeaderboard && (
                                            <TabButton
                                                isSelected={showingLeaderboard}
                                                text={<span className="fal fa-ranking-star"></span>}
                                                tooltip="Leaderboard"
                                                onClick={() => {
                                                    setSelectedPlayer(undefined);
                                                    setSelectedRound(undefined);
                                                    setShowingPopularReplies(false);
                                                    setShowingLeaderboard(true);
                                                    setShowingRewrite(false);
                                                }}
                                            />
                                        )}

                                        <TabButton
                                            isSelected={showingRewrite}
                                            text={<span className="fal fa-edit"></span>}
                                            tooltip="My version"
                                            onClick={() => {
                                                setSelectedPlayer(undefined);
                                                setSelectedRound(undefined);
                                                setShowingPopularReplies(false);
                                                setShowingLeaderboard(false);
                                                setShowingRewrite(true);
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <TabsPlaceholder />
                                )}

                                <div className="h4 hstack gap-2 lh-1">
                                    {columnTitleIcon}
                                    {columnTitle}
                                </div>
                            </div>

                            {showingRewrite && gameState ? (
                                <RewriteStory gameState={gameState} playerState={playerState} />
                            ) : showingLeaderboard && gameState ? (
                                <Leaderboard
                                    gameState={gameState}
                                    selectPlayer={selectPlayer}
                                    playerNames={playerNames}
                                />
                            ) : showingPopularReplies && gameState ? (
                                <div className="vstack gap-4">
                                    {storyAlternatives.map(reply => (
                                        <div className="completed-story" key={reply.id}>
                                            <div className="order-1 order-lg-0 vstack gap-2">
                                                <div className="h6 text-center text-lg-start">
                                                    {reply.isTitleRound ? (
                                                        <Fragment>Title round</Fragment>
                                                    ) : (
                                                        <Fragment>Round {reply.roundNumber}</Fragment>
                                                    )}
                                                </div>

                                                <ReplyDisplay
                                                    body={reply.body}
                                                    isPerfect={reply.isPerfect}
                                                    hasSticker={reply.hasSticker}
                                                    url={
                                                        (playerState?.isPlayer || playerState?.isLeader) &&
                                                        gameState?.groupId &&
                                                        reply.userId
                                                            ? RouteLink.classesClassStudent(
                                                                  gameState.groupId,
                                                                  reply.userId,
                                                              )
                                                            : undefined
                                                    }
                                                />
                                            </div>

                                            <div>
                                                {gameState?.players && gameState.players.length > 1 && (
                                                    <StoryDetails
                                                        characterId={reply.characterId}
                                                        playerName={playerNames[reply.playerId]}
                                                        reactionCount={reply.reactionCount}
                                                        voteCount={getVoteCount(reply.id)}
                                                        isPerfect={reply.isPerfect}
                                                        url={
                                                            (playerState?.isPlayer || playerState?.isLeader) &&
                                                            gameState?.groupId &&
                                                            reply.userId
                                                                ? RouteLink.classesClassStudent(
                                                                      gameState.groupId,
                                                                      reply.userId,
                                                                  )
                                                                : undefined
                                                        }
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="w-100">
                                    {gameState && selectedReplies.length !== 0 ? (
                                        <div className="vstack gap-3 w-100">
                                            {roundInstruction && <div className="h4">{roundInstruction}</div>}

                                            <div className="vstack gap-4 gap-lg-5 w-100">
                                                <StoryRepliesDisplay
                                                    showDetails={gameState.players.length > 1}
                                                    selectedReplies={selectedReplies}
                                                    showThread={!selectedPlayer && !selectedRound}
                                                    selectedPlayer={selectedPlayer}
                                                    getVoteCount={getVoteCount}
                                                    playerNames={playerNames}
                                                    groupId={gameState.groupId}
                                                    playerState={playerState}
                                                />

                                                {!isAuthenticated && (
                                                    <div className="d-flex flex-column flex-lg-row align-items-center gap-2 gap-lg-3">
                                                        <div>Want to make your own Frankenstory game?</div>
                                                        <div>
                                                            <Link
                                                                to={RouteList.Signup}
                                                                className="btn btn-primary icon-link"
                                                            >
                                                                <IconText
                                                                    icon="fas fa-user-plus"
                                                                    text="Create account"
                                                                />
                                                            </Link>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="grid gap-4">
                                            <StoryItemPlaceholder />
                                            <StoryItemPlaceholder />
                                            <StoryItemPlaceholder />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {playerState?.llmScore && (
                <ReportCardModal
                    dimensions={playerState.dimensions}
                    llmScore={playerState.llmScore}
                    playerName={globalData?.firstName ?? playerState.playerName ?? "You"}
                />
            )}
        </div>
    );
};
